export const WEB_SUMMIT_SHARED_DEMO_CASE_ID = 'websummit-rtsp-pedestrian-forklift';
export const LEGACY_WEB_SUMMIT_DEMO_CASE_ID = 'websummit-yyz-forklift-aisle-2';
export const LEGACY_WEB_SUMMIT_DEMO_CASE_ID_ALT = 'web-summit-pedestrian-zone';
export const DEFAULT_WEB_SUMMIT_SCENARIO_ID = 'pedestrian-forklift';
export const LEGACY_WEB_SUMMIT_SCENARIO_ID = 'pedestrian-forklift-interaction';
export const WEB_SUMMIT_DEMO_CAMERA_SOURCE = 'local-rtsp-demo';

export type DemoStatusCard = {
  title: string;
  status: string;
};

export type DemoWorkflowStep = {
  title: string;
  detail: string;
};

export type WebSummitDemoScenario = {
  id: string;
  aliases?: string[];
  family: string;
  pattern: string;
  incidentType: string;
  site: string;
  siteId: string;
  zone: string;
  zoneId: string;
  windowLabel: string;
  description: string;
  cameraSource: string;
  cameraLabel: string;
  modeLabel: string;
  statusFlow: string[];
  statusCards: DemoStatusCard[];
  walkthroughSteps: DemoWorkflowStep[];
};

export const webSummitDemoScenarios: WebSummitDemoScenario[] = [
  {
    id: DEFAULT_WEB_SUMMIT_SCENARIO_ID,
    aliases: [LEGACY_WEB_SUMMIT_SCENARIO_ID],
    family: 'Pedestrian / Forklift Interaction',
    pattern: 'Pedestrian activity in forklift operating area',
    incidentType: 'zone_violation',
    site: 'WebSummit Demo',
    siteId: 'yyz',
    zone: 'Forklift operating area',
    zoneId: 'forklift-operating-area',
    windowLabel: 'Live demo session',
    description:
      'A local RTSP camera feed is monitored for pedestrian activity inside a forklift operating area, then converted into a reviewable incident and proof-ready workflow.',
    cameraSource: WEB_SUMMIT_DEMO_CAMERA_SOURCE,
    cameraLabel: 'Local RTSP demo feed',
    modeLabel: 'Looping demo feed',
    statusFlow: [
      'Camera feed connected',
      'Risk detected',
      'Incident created',
      'Evidence reviewed',
      'Action logged',
      'Proof package prepared',
    ],
    statusCards: [
      { title: 'Camera feed', status: 'Connected' },
      { title: 'Detection', status: 'Active' },
      { title: 'Incident', status: 'Created' },
      { title: 'Review', status: 'Ready' },
      { title: 'Proof', status: 'Ready to prepare' },
    ],
    walkthroughSteps: [
      {
        title: 'Camera feed connected',
        detail: 'Local camera stream available for analysis.',
      },
      {
        title: 'Risk detected',
        detail: 'Pedestrian activity identified in forklift operating area.',
      },
      {
        title: 'Incident created',
        detail: 'Detection becomes a reviewable danger-zone observation.',
      },
      {
        title: 'Evidence reviewed',
        detail: 'The incident can be inspected and verified in Review.',
      },
      {
        title: 'Action logged',
        detail: 'Corrective follow-up can be recorded.',
      },
      {
        title: 'Proof package prepared',
        detail: 'Evidence package can be prepared for handoff.',
      },
    ],
  },
];

export function isWebSummitScenarioCaseId(caseIdRaw: string | null | undefined): boolean {
  const caseId = String(caseIdRaw || '').trim();
  return (
    caseId === WEB_SUMMIT_SHARED_DEMO_CASE_ID ||
    caseId === LEGACY_WEB_SUMMIT_DEMO_CASE_ID ||
    caseId === LEGACY_WEB_SUMMIT_DEMO_CASE_ID_ALT
  );
}

export function getWebSummitDemoScenarioById(scenarioIdRaw: string | null | undefined): WebSummitDemoScenario {
  const scenarioId = String(scenarioIdRaw || '').trim();
  return (
    webSummitDemoScenarios.find(
      (scenario) => scenario.id === scenarioId || Boolean(scenario.aliases?.includes(scenarioId)),
    ) || webSummitDemoScenarios[0]
  );
}

function buildScenarioQueryParams(search: string, scenarioIdRaw?: string | null): URLSearchParams {
  const scenario = getWebSummitDemoScenarioById(scenarioIdRaw);
  const params = new URLSearchParams(search);
  params.set('scenarioId', scenario.id);
  params.set('scenario_id', scenario.id);
  params.set('caseId', WEB_SUMMIT_SHARED_DEMO_CASE_ID);
  params.set('case_id', WEB_SUMMIT_SHARED_DEMO_CASE_ID);
  params.set('site', scenario.siteId);
  params.set('site_id', scenario.siteId);
  params.set('zone_id', scenario.zoneId);
  params.set('incident_type', scenario.incidentType);
  params.set('cameraSource', scenario.cameraSource);
  params.set('camera_source', scenario.cameraSource);
  if (!params.get('report_from')) params.set('report_from', '2026-05-07T09:00');
  if (!params.get('report_to')) params.set('report_to', '2026-05-07T09:15');
  return params;
}

export function buildAcceptanceImpactPath(search = ''): string {
  const params = buildScenarioQueryParams(search, DEFAULT_WEB_SUMMIT_SCENARIO_ID);
  const query = params.toString();
  return query ? `/acceptance/impact?${query}` : '/acceptance/impact';
}

export function buildAcceptanceWalkthroughPath(scenarioIdRaw?: string | null, search = ''): string {
  const scenario = getWebSummitDemoScenarioById(scenarioIdRaw);
  const query = buildScenarioQueryParams(search, scenario.id).toString();
  return query
    ? `/acceptance/impact/walkthrough/${encodeURIComponent(scenario.id)}?${query}`
    : `/acceptance/impact/walkthrough/${encodeURIComponent(scenario.id)}`;
}

export function buildAcceptanceScenarioReviewPath(scenarioIdRaw?: string | null, search = ''): string {
  const scenario = getWebSummitDemoScenarioById(scenarioIdRaw);
  const params = buildScenarioQueryParams(search, scenario.id);
  params.set('tab', 'open');
  params.set('table_mode', 'grouped');
  params.set('focus', 'queue');
  params.set('workflow', 'live_rtsp_demo');
  const query = params.toString();
  return query ? `/incidents?${query}` : '/incidents';
}

export function buildAcceptanceScenarioProofPath(scenarioIdRaw?: string | null, search = ''): string {
  const params = buildScenarioQueryParams(search, scenarioIdRaw);
  const query = params.toString();
  return query ? `/acceptance/proof?${query}` : '/acceptance/proof';
}
